import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
export default function Terms() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-gradient-to-br from-background via-background to-background/80">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-bold text-foreground mb-8">
              CraftyJoy Terms of Service & Privacy Policy
            </h1>
            
            <p className="text-muted-foreground mb-6">
              <strong>Last Updated:</strong> July 25, 2025
            </p>

            <p className="text-foreground leading-relaxed mb-6">
              Welcome to CraftyJoy, an interactive platform that transforms everyday objects into creative DIY ideas. Please read the following Terms of Service and Privacy Policy ("Agreement") carefully before using the CraftyJoy mobile app, website, or any associated features ("Service").
            </p>

            <p className="text-foreground leading-relaxed mb-8">
              By accessing or using CraftyJoy, you agree to be legally bound by this Agreement. If you do not agree to these terms, you must not access or use the Service.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Eligibility & Acceptance</h2>
            <p className="text-foreground leading-relaxed mb-4">By using CraftyJoy, you confirm that:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>You are at least 13 years of age (or older depending on your country's law).</li>
              <li>You have the legal capacity to enter into a binding agreement.</li>
              <li>If you are using CraftyJoy on behalf of an organization, you have authority to bind it to these terms.</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">
              <strong>For Users Under 18:</strong> We strongly recommend parental supervision when using the Service, especially for projects involving tools, chemicals, or other potentially hazardous materials.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. Service Overview</h2>
            <p className="text-foreground leading-relaxed mb-4">CraftyJoy allows users to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Upload photos containing various items.</li>
              <li>Receive DIY crafting ideas based on recognized objects in the image.</li>
              <li>Save or share suggested projects.</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">
              These suggestions are generated by AI and/or curated by our team. We do not guarantee the accuracy, usability, or safety of the project suggestions. Users must exercise caution and judgment when attempting any project.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. User Responsibilities</h2>
            <p className="text-foreground leading-relaxed mb-4">By using our Service, you agree to:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground">
              <li>Only upload images and content that you legally own or have permission to use.</li>
              <li>Use the platform solely for lawful, non-harmful purposes.</li>
              <li>Avoid uploading any prohibited content as defined below.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Prohibited Use & Zero Tolerance Policy</h2>
            <p className="text-foreground leading-relaxed mb-4">You must not upload or distribute any content that includes or promotes:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Weapons (e.g., guns, knives, explosives)</li>
              <li>Illegal drugs, controlled substances, or paraphernalia</li>
              <li>Sexually explicit, pornographic, or obscene content</li>
              <li>Hate speech, threats, or harassment</li>
              <li>Dangerous tools (e.g., power saws, blowtorches), chemicals, or instructions for illegal activities</li>
              <li>Self-harm, violence, or suicide-related material</li>
              <li>Copyrighted content you do not own</li>
              <li>Any material that violates local, state, national, or international law</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-4">
              Violating this policy will result in immediate suspension or permanent deletion of your account. We may report content or users to law enforcement if necessary. CraftyJoy reserves the sole right to decide what constitutes a violation.
            </p>
            <p className="text-foreground leading-relaxed mb-6">
              <strong>Appeal Process:</strong> If you believe your account was wrongfully suspended or terminated, you may appeal by emailing support@craftyjoy.app within 30 days.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Content Ownership & License</h2>
            <p className="text-foreground leading-relaxed mb-4">
              You retain ownership of the images and content you upload. By uploading content to CraftyJoy, you grant us a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Host, display, and process your images</li>
              <li>Generate derivative works (i.e., suggestions)</li>
              <li>Use your content for product improvement, research, and promotional purposes (unless you opt out)</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">
              <strong>Plain Language Explanation:</strong> Your photos may be used to improve our AI or promote CraftyJoy on social media unless you opt out via app settings.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Safety Warnings</h2>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
              <p className="text-destructive font-semibold mb-2">⚠️ IMPORTANT: USE AT YOUR OWN RISK</p>
              <p className="text-foreground leading-relaxed">
                CraftyJoy provides DIY project suggestions that may involve inherent risks, including but not limited to:
              </p>
            </div>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Use of sharp tools (e.g., scissors, knives)</li>
              <li>Handling of chemicals or flammable materials</li>
              <li>Exposure to allergens or toxic substances</li>
              <li>Risk of burns, cuts, fires, choking, or electric shock</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-4">
              <strong>User Responsibility:</strong> You are solely responsible for your safety and the safety of others when attempting any project. Always take appropriate precautions, such as:
            </p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Wearing protective gear (e.g., gloves, goggles)</li>
              <li>Working in well-ventilated areas</li>
              <li>Supervising minors or inexperienced users</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-4">
              If you are unsure about the safety or legality of a project, do not attempt it. Consult a professional if necessary.
            </p>
            <p className="text-foreground leading-relaxed mb-6">
              <strong>Liability Disclaimer:</strong> CraftyJoy is not liable for any injury, accident, property damage, legal consequences, or negative outcome resulting from your use of the Service. By using CraftyJoy, you agree to hold us harmless from any harm, losses, or legal consequences.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. No Refunds Policy</h2>
            <p className="text-foreground leading-relaxed mb-4">By purchasing any subscription, credit pack, or premium service:</p>
            <ul className="list-disc pl-6 mb-6 text-foreground">
              <li>You agree that all purchases are final and non-refundable.</li>
              <li>We are not responsible for dissatisfaction, accidental purchases, limited use, or changes to your usage status.</li>
              <li>Subscriptions may auto-renew unless cancelled before the renewal date.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Termination of Account</h2>
            <p className="text-foreground leading-relaxed mb-4">We may suspend or terminate your account without notice if:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>You violate this Agreement</li>
              <li>You upload prohibited content</li>
              <li>You engage in fraudulent, harmful, or unlawful activity</li>
              <li>We receive complaints or are required by law</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">
              Termination may include deletion of your account, content, and access history. If your account is terminated, you may request retrieval of non-violating content by emailing support@craftyjoy.app.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. Modifications to Service & Terms</h2>
            <p className="text-foreground leading-relaxed mb-4">We reserve the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Modify, update, or discontinue the Service at any time</li>
              <li>Update this Agreement for legal, functional, or user experience reasons</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">
              We will provide at least 30 days' notice of material changes via email or in-app notification. Continued use of CraftyJoy constitutes acceptance of updated terms.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. Intellectual Property</h2>
            <p className="text-foreground leading-relaxed mb-6">
              All app content, including AI-generated suggestions, design elements, text, icons, and logos, are the property of CraftyJoy and its licensors. You may not copy, resell, or redistribute any part of the Service without prior written permission.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">11. Indemnification</h2>
            <p className="text-foreground leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless CraftyJoy, its officers, directors, employees, contractors, and affiliates from any claim, liability, loss, damage, or expense resulting from:
            </p>
            <ul className="list-disc pl-6 mb-6 text-foreground">
              <li>Your willful misuse of the Service</li>
              <li>Any content you upload that violates these Terms</li>
              <li>Your violation of applicable laws</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">12. Governing Law</h2>
            <p className="text-foreground leading-relaxed mb-4">These Terms shall be governed and interpreted in accordance with the laws of the State of Florida, United States. Any disputes shall be resolved in the courts located in Miami, Florida.</p>
            <p className="text-foreground leading-relaxed mb-6">
              <strong>For International Users:</strong> While we aim to resolve disputes in Florida, some jurisdictions may allow claims to be brought locally.
            </p>

            <h1 className="text-3xl font-bold text-foreground mt-12 mb-8">🔒 Privacy Policy</h1>
            <p className="text-foreground leading-relaxed mb-6">
              CraftyJoy is committed to protecting your privacy. This section explains how we collect, use, and protect your information.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Information We Collect</h2>
            <ul className="list-disc pl-6 mb-6 text-foreground">
              <li><strong>Information You Provide:</strong> Name, email, login credentials, uploaded photos (with metadata removed for privacy), project interactions.</li>
              <li><strong>Automatically Collected Data:</strong> IP address, approximate location, device type, OS, app usage data.</li>
              <li><strong>Third-Party Integrations:</strong> Limited profile data if you sign in via Apple, Google, or Facebook.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. How We Use Your Data</h2>
            <p className="text-foreground leading-relaxed mb-4">We use your data to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Provide and personalize the app experience</li>
              <li>Generate object-based project ideas</li>
              <li>Improve our AI algorithms (photos may be used for AI training unless you opt out)</li>
              <li>Detect misuse, bugs, or abuse</li>
              <li>Communicate with you about updates, support, and offers</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. Data Sharing</h2>
            <p className="text-foreground leading-relaxed mb-4">
              We do not sell your personal data. We may share information with:
            </p>
            <ul className="list-disc pl-6 mb-6 text-foreground">
              <li>Cloud and analytics services (e.g., Firebase, AWS)</li>
              <li>Payment processors</li>
              <li>Law enforcement if legally required</li>
              <li>Successor entities in case of acquisition (with notice where feasible)</li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Your Rights</h2>
            <p className="text-foreground leading-relaxed mb-4">You may:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Access or update your personal info</li>
              <li>Request deletion of your account and content</li>
              <li>Opt out of promotional emails or AI training use</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-6">To exercise these rights, email support@craftyjoy.app. We will respond within 30 days.</p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Data Security</h2>
            <p className="text-foreground leading-relaxed mb-6">
              We use industry-standard practices (e.g., SSL encryption, secure servers) to protect your data. However, no system is 100% secure.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Children's Privacy</h2>
            <p className="text-foreground leading-relaxed mb-6">
              CraftyJoy is not directed at children under 13. If we discover that a minor has provided personal data without parental consent, we will delete the information immediately.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. International Users</h2>
            <p className="text-foreground leading-relaxed mb-6">
              Your data may be processed in the United States or other jurisdictions.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Data Retention</h2>
            <p className="text-foreground leading-relaxed mb-6">
              We retain your information as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce agreements. Inactive accounts may be deleted after 2 years unless legally required to retain data.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. Contact Information</h2>
            <p className="text-foreground leading-relaxed mb-6">
              For legal questions or concerns: support@craftyjoy.app
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Cancellation Policy</h2>
            <p className="text-foreground leading-relaxed mb-4">To cancel your subscription or account, email support@craftyjoy.app with the following information:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground">
              <li>Your account email address</li>
              <li>A clear statement of your intent to cancel</li>
            </ul>
            <p className="text-foreground leading-relaxed mb-8">
              We will process your cancellation within 5 business days. Note: Cancellation does not entitle you to a refund unless required by law.
            </p>
          </div>
        </div>
      </div>
    </div>;
}